/**
 * Coach-facing copy for the travel-distance (body-height) target — the
 * Movement Goal panel's authoring controls and the evaluation report's
 * travel result row.
 *
 * SINGLE SOURCE of every string this check renders, panel or report, per
 * `docs/coach_input/TRAVEL_PR_A_CONTRACT.md` §5 (the ownership table: the
 * backend supplies the NUMBERS, the frontend supplies the SENTENCE) and
 * `docs/COACH_FACING_COPY_STANDARD.md` (every string answers what / why /
 * how, in a non-technical reader's own words — no landmark names, no
 * statistics, no internal vocabulary). Pinned by
 * `frontend/src/test/coachCopyDenylist.ts`.
 *
 * Numbering below follows the plan's own §7.4 numbering (also reproduced in
 * the contract's §5 table) so a reviewer can cross-reference directly.
 * String #3 (the multiplier bar, "ask for more than my demo") and #8 (the
 * athlete-upload camera-framing tip) are deliberately NOT built here — #3
 * is an owner-optional convenience the panel does not expose (owner's flow
 * is a strict two-way choice, contract §1.1/§5), and #8 belongs to the
 * video-upload step, not this panel (contract §5 row 8).
 */

/**
 * The enforced tolerance floor (contract §3's suggested copy / plan §6.4,
 * re-derived here as `0.20 x target` per
 * `backend/app/services/body_height.py::TRAVEL_TOLERANCE_MIN_FRACTION_OF_TARGET`
 * — SAME constant, duplicated client-side because the authoring-time floor
 * must be shown before a score exists to read it off. If that backend
 * constant ever changes, this one must change with it; there is no live
 * endpoint that returns it directly at authoring time (contract §5 row 6).
 */
export const TRAVEL_TOLERANCE_MIN_FRACTION_OF_TARGET = 0.20;

/** Two decimal places, no more — per the contract's own rounding rule (§2):
 * at the measurement's ~8-9% agreement, a third decimal is noise. */
export function formatBodyHeights(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toFixed(2);
}

/** #1 — panel title + subtitle. `bodyPartLabel` is the plain-English point
 * name ("Right Wrist") when a landmark has been picked, else a generic
 * stand-in — never a raw landmark identifier. */
export function travelPanelTitle(): string {
  return 'Travel distance';
}

export function travelPanelSubtitle(bodyPartLabel: string | null | undefined): string {
  const point = bodyPartLabel && bodyPartLabel.trim().length > 0 ? bodyPartLabel : 'the point you pick';
  return `How far the ${point} travels, measured against how tall the person looks in their own video.`;
}

export const TRAVEL_PANEL_WHY =
  'That way a tall coach and a shorter athlete can be compared without anyone measuring anybody.';

export const TRAVEL_PANEL_HOW = 'Pick the point on the video and set the target below.';

// ---------------------------------------------------------------------------
// #2 / #5 — the A/B source bar
// ---------------------------------------------------------------------------

export const TRAVEL_SOURCE_DEMO_LABEL = 'Use my demo';
export const TRAVEL_SOURCE_DEMO_WHY =
  'We measure your own travel and ask the athlete for the same share.';

export const TRAVEL_SOURCE_TYPED_LABEL = "I'll type the target myself";
export const TRAVEL_SOURCE_TYPED_WHY = "Useful if your demo isn't the bar you want to set.";
export const TRAVEL_SOURCE_TYPED_HOW =
  'Type a share of how tall they look — the number beside your demo shows the scale.';

// ---------------------------------------------------------------------------
// #4 — demo measured line
// ---------------------------------------------------------------------------

export function demoMeasuredLine(ratio: number | null | undefined): string {
  return `Your demo travels ${formatBodyHeights(ratio)}.`;
}

export const TRAVEL_DEMO_MEASURED_HOW = "Nothing to do — this is the number we'd use.";

// ---------------------------------------------------------------------------
// #6 — the enforced tolerance floor, stated (not a warning the coach can
// override — the owner's 2026-10-09 ruling).
// ---------------------------------------------------------------------------

export function travelToleranceFloorStatement(floorBodyHeights: number): string {
  return (
    `We never score this tighter than ${formatBodyHeights(floorBodyHeights)} — two takes of ` +
    'the same movement can differ by about that much on their own.'
  );
}

/** `target` is the best number the panel has for the bar right now (the
 * coach's typed value, else the demo's measured ratio). Returns `null` when
 * nothing is known yet — the panel renders nothing in that case. */
export function computeTravelToleranceFloor(target: number | null | undefined): number | null {
  if (target === null || target === undefined || !Number.isFinite(target) || target <= 0) {
    return null;
  }
  return TRAVEL_TOLERANCE_MIN_FRACTION_OF_TARGET * target;
}

// ---------------------------------------------------------------------------
// #6a — importance hint
// ---------------------------------------------------------------------------

export const TRAVEL_IMPORTANCE_HINT = 'How much this check counts is set by its importance below.';
export const TRAVEL_IMPORTANCE_WHY =
  'Set it higher if this is one of the things you most want to see in the score.';

// ---------------------------------------------------------------------------
// #7 — above-demo notice
// ---------------------------------------------------------------------------

export const TRAVEL_ABOVE_DEMO_NOTICE = "You've asked for more than your own demo shows.";
export const TRAVEL_ABOVE_DEMO_HOW = "That's fine — change it if it wasn't intended.";

// ---------------------------------------------------------------------------
// Travel PR-B item 4 — "the honest guidance" (plan §4.5, the price of the
// enforced tolerance floor). A short, always-shown note on when this check
// will not tell the coach much: a movement whose own travel is close to the
// floor the panel already enforces (contract §3 `tolerance_floor_applied`).
// No numbers beyond "a fifth of the target" — the floor's own fraction
// (`TRAVEL_TOLERANCE_MIN_FRACTION_OF_TARGET`), never a literal percentage.
// ---------------------------------------------------------------------------

export const TRAVEL_SMALL_MOVEMENT_NOTE = 'This check tells you less for a small movement.';
export const TRAVEL_SMALL_MOVEMENT_WHY =
  'A movement smaller than about a fifth of the target can be hard to tell apart from the ordinary camera wobble between two takes of the same movement.';
export const TRAVEL_SMALL_MOVEMENT_HOW =
  'Use it for a clear, sizeable movement, and read a small-movement result as a rough guide rather than an exact mark.';

// ---------------------------------------------------------------------------
// #9 — the window line (only when no phase is selected)
// ---------------------------------------------------------------------------

export const TRAVEL_WINDOW_LINE = 'We measure from the start of your clip to the end.';
export const TRAVEL_WINDOW_HOW = 'Trim the clip to the movement itself.';

// ---------------------------------------------------------------------------
// #10 — report row
// ---------------------------------------------------------------------------

export function travelReportRow(
  checkpointLabel: string,
  measuredBodyHeights: number | null | undefined,
  targetBodyHeights: number | null | undefined,
): string {
  return (
    `'${checkpointLabel}': travelled ${formatBodyHeights(measuredBodyHeights)} — ` +
    `you asked for ${formatBodyHeights(targetBodyHeights)}.`
  );
}

// ---------------------------------------------------------------------------
// Report: plain-language source + body-height-definition lines.
//
// `target_source_resolved` and `body_height_definition_used` are both
// INTERNAL vocabulary on the wire (contract §3) — never rendered as the raw
// enum value. These two helpers are the only place that reads them.
// ---------------------------------------------------------------------------

export function travelTargetSourcePhrase(
  source: 'coach' | 'demo_multiple' | 'demo' | null | undefined,
): string {
  switch (source) {
    case 'coach':
      return 'the target you set';
    case 'demo_multiple':
      return "a multiple of your own demo";
    case 'demo':
    default:
      return 'your own demo';
  }
}

/** Same plain sentence regardless of which internal definition the backend
 * used (composed_stack / nose_to_lowest_foot) — both answer the same
 * question ("how tall do they look head-to-foot") and the distinction is
 * never useful to a coach. */
export const TRAVEL_BODY_HEIGHT_DEFINITION_LINE =
  'Measured against how tall they look from head to feet in their own video.';

// ---------------------------------------------------------------------------
// Floor-applied note on a SCORED result (contract §3's suggested copy for
// `tolerance_floor_applied: true`).
// ---------------------------------------------------------------------------

export function travelFloorAppliedNote(effectiveToleranceBodyHeights: number | null | undefined): string {
  return (
    `We never score this tighter than ${formatBodyHeights(effectiveToleranceBodyHeights)} — ` +
    'two takes of the same movement can differ by about that much on their own.'
  );
}
